import { NewChat } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { useNewChatMatterBoundAction } from '@/chat-common/new_chat/hooks/useNewChatMatterBoundAction'
import styles from './AgentV2StaleLawBanner.module.css'

/**
 * Conversation-level notice that at least one law cited in this chat has moved.
 *
 * Uses an independent check of all law-reference hashes in the conversation, including
 * unloaded history. The notice appears when that asynchronous check completes.
 * Individual reference badges retain their own status enrichment.
 *
 * Citations are snapshots and are never rewritten in place, so the only remedy is a fresh
 * chat, which re-runs the research against the current law.
 *
 * Owns its centering on the chat's max-w-3xl column rather than inheriting a width from the
 * mount site, as ViewOnlyChatNotice and the composer each do.
 */
export function AgentV2StaleLawBanner({ hasStaleLaw }: { hasStaleLaw: boolean | null }) {
  const { handleButtonClick, clickDisabled, createError } = useNewChatMatterBoundAction()

  if (!hasStaleLaw) return null

  return (
    <div className={`${styles.reveal} mx-auto w-full max-w-3xl`}>
      <div className="min-h-0 overflow-hidden">
        <div className="pb-2">
          <InlineMessage variant="neutral">
            <div className="flex min-w-0 items-center justify-between gap-2">
              <span>Laws referenced in this chat may have been updated. Start a new chat for updated references.</span>
              <Button
                className="shrink-0"
                color="primary"
                disabled={clickDisabled}
                leadingIcon={<NewChat className="h-3.5 w-3.5 shrink-0" aria-hidden />}
                onClick={handleButtonClick}
                size="xs"
                variant="plain"
              >
                New Chat
              </Button>
            </div>
          </InlineMessage>
          {/* Same inline error the other three new-chat entry points render; creating a chat
            never reports through a toast (ENG-7070). */}
          {createError && (
            <div className="mt-1">
              <InlineMessage variant="error">Error creating chat. Please try again.</InlineMessage>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
